import { ShieldCheck, CheckCircle2, Rocket, ArrowRight, Cpu } from "lucide-react";
import {
  FLOW_STEPS,
  PHASE_ORDER,
  PIPELINE_STAGES,
  AUTO_REJECTIONS,
  CROSS_SOURCE_NOTE,
  VALIDATION_PARTIES,
  ROLE_META,
} from "./flowData";
import { HeroJourney } from "./HeroJourney";
import { AutomationSummary } from "./AutomationSummary";
import { PhaseBanner } from "./PhaseBanner";
import { FlowStepCard } from "./FlowStepCard";
import { TimelineConnector } from "./TimelineConnector";
import { PipelineOverview } from "./PipelineOverview";
import { PipelinePhaseCard } from "./PipelinePhaseCard";
import { RejectionBranch } from "./RejectionBranch";
import { ImpactMetrics } from "./ImpactMetrics";

interface HowItWorksContentProps {
  /** "live" pulls aggregate stats from the DB (in-app); "static" uses the curated snapshot (public). */
  metricsMode: "live" | "static";
}

/**
 * The nested AI scan-pipeline deep-dive rendered under the "Web research" step.
 * Mirrors the real six-stage pipeline (PHASE_ORDER in src/server/scanner/pipeline.ts)
 * followed by the auto-rejection pass.
 */
function AiPipelineDeepDive() {
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <Cpu className="size-4 text-violet-600" />
        <h4 className="text-sm font-semibold text-foreground">
          Inside the AI scan — six stages
        </h4>
      </div>

      {/* Compact glance */}
      <PipelineOverview />

      {/* Stage-by-stage */}
      <div className="space-y-0">
        {PIPELINE_STAGES.map((stage, i) => (
          <div key={stage.stage}>
            <PipelinePhaseCard stage={stage} />
            {i < PIPELINE_STAGES.length - 1 && <TimelineConnector />}
          </div>
        ))}
      </div>

      {/* Auto-rejection pass */}
      <TimelineConnector />
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Then: a single auto-rejection pass
        </p>
        <RejectionBranch rules={AUTO_REJECTIONS} hideConnector />
        <div className="flex items-start gap-2 pt-1">
          <ShieldCheck className="size-4 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            <span className="font-medium text-amber-600">Cross-source protection:</span>{" "}
            {CROSS_SOURCE_NOTE}
          </p>
        </div>
      </div>
    </div>
  );
}

/** Closing callout: a station ships only after all three parties sign off. */
function TriPartyValidation() {
  return (
    <div className="rounded-xl border border-border bg-muted/40 p-6">
      <h3 className="text-base font-semibold text-foreground">
        Validated by all three before it ships
      </h3>
      <p className="text-sm text-muted-foreground mt-1">
        Every station that reaches the Equip Dashboard has been signed off independently.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {VALIDATION_PARTIES.map((role) => {
          const meta = ROLE_META[role];
          const Icon = meta.icon;
          return (
            <div
              key={role}
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium ${meta.bg} ${meta.text}`}
            >
              <Icon className="size-4" />
              {meta.label}
              <CheckCircle2 className="size-4" />
            </div>
          );
        })}
        <ArrowRight className="size-5 text-muted-foreground" />
        <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1.5 text-sm font-medium text-emerald-700">
          <Rocket className="size-4" />
          Equip Dashboard
        </div>
      </div>
    </div>
  );
}

/**
 * Full "How it works" page content, shared by the in-app and public routes.
 * Hero overview → two-phase timeline (with the AI pipeline nested under step 2)
 * → tri-party validation → impact metrics.
 */
export function HowItWorksContent({ metricsMode }: HowItWorksContentProps) {
  return (
    <div className="max-w-5xl mx-auto space-y-12">
      {/* Header */}
      <header>
        <h1 className="text-2xl md:text-3xl font-bold text-foreground">
          How Equip Scout works
        </h1>
        <p className="text-muted-foreground mt-2 max-w-3xl leading-relaxed">
          From a blank map to validated sport-station locations — a proprietary
          AI pipeline that discovers and scores candidates, then on-the-ground
          verification by real people. Two phases:{" "}
          <span className="font-medium text-foreground">Digital Mapping</span>{" "}
          and <span className="font-medium text-foreground">Physical Mapping</span>.
        </p>
      </header>

      {/* Hero glance */}
      <HeroJourney />

      {/* "Why it matters" — automate-the-hard-part summary */}
      <AutomationSummary />

      {/* Two-phase timeline */}
      <div className="space-y-10">
        {PHASE_ORDER.map((phase, idx) => {
          const steps = FLOW_STEPS.filter((s) => s.phase === phase);
          return (
            <section
              key={phase}
              className={`space-y-6${idx > 0 ? " pt-6" : ""}`}
            >
              <PhaseBanner phase={phase} />
              <div className="space-y-0">
                {steps.map((step, i) => (
                  <div key={step.number}>
                    <FlowStepCard step={step}>
                      {step.hasDeepDive && <AiPipelineDeepDive />}
                    </FlowStepCard>
                    {i < steps.length - 1 && <TimelineConnector />}
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {/* Tri-party validation callout */}
      <TriPartyValidation />

      {/* Impact metrics finale */}
      <section className="space-y-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground">
            Equip Scout by the numbers
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {metricsMode === "live"
              ? "Live aggregates across every scan and city processed to date."
              : "A snapshot of what the platform has produced to date."}
          </p>
        </div>
        <ImpactMetrics mode={metricsMode} />
      </section>
    </div>
  );
}
